import { type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useRef, useState } from 'react';
import { errorMessage } from '../../api/client';
import { shrinkPhoto } from '../../api/shrink';
import type { Media } from '../../api/types';
import { ACCEPTED_IMAGES, uploadImage } from '../../api/upload';
import { IconImage, IconMove, IconUpload } from '../../components/icons';
import { formatBytes, useFeedback } from '../../components/ui';
import { useT } from '../../i18n';
import { CARD_RATIO, cardCropWidth, OG_RATIO } from './shared';

type Crop = { x: number; y: number };

interface PmPhotoProps {
  media: Media | null;
  crop: Crop;
  onMedia: (m: Media) => void;
  onCrop: (c: Crop) => void;
  error?: string;
  /** La línea punteada de la imagen para compartir (las noticias la tienen; la historia no). */
  ogGuide?: boolean;
  /** Cómo se ve la foto en el sitio con este encuadre. */
  previews: (media: Media, crop: Crop) => ReactNode;
}

/**
 * La foto de una noticia o de un hito de la historia: se sube (achicada a 2400 px si es de cámara),
 * se encuadra arrastrando el recuadro de la tarjeta (700 × 437) y se ve cómo queda en el sitio.
 */
export function PmPhoto({ media, crop, onMedia, onCrop, error, ogGuide = false, previews }: PmPhotoProps) {
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const { toast } = useFeedback();
  const { t } = useT();

  const upload = async (file: File | undefined) => {
    if (!file) return;
    setProgress(t('Preparando…'));
    try {
      // Las fotos grandes (de cámara) se achican a 2400 px antes de subir.
      const ready = await shrinkPhoto(file);
      onMedia(await uploadImage(ready, (p) => setProgress(t('Subiendo… {pct} %', { pct: Math.round(p * 100) }))));
    } catch (e) {
      toast(`${file.name}: ${errorMessage(e)}`, 'error');
    } finally {
      setProgress(null);
    }
  };

  const pick = (
    <input
      ref={input}
      type="file"
      accept={ACCEPTED_IMAGES}
      hidden
      onChange={(e) => {
        void upload(e.target.files?.[0]);
        e.target.value = '';
      }}
    />
  );

  if (!media) {
    return (
      <div className="field">
        <div
          className={`slot ${over ? 'drop' : ''}`}
          style={{ aspectRatio: '700 / 437' }}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void upload(e.dataTransfer.files[0]);
          }}
        >
          <button type="button" className="slot-empty" onClick={() => input.current?.click()} disabled={!!progress}>
            <IconImage />
            <span>{progress ?? t('Arrastra la foto o haz clic')}</span>
            <span className="faint" style={{ fontSize: 12 }}>
              {t('JPG, PNG o WebP · ideal 1400 × 874 o más')}
            </span>
          </button>
        </div>
        {error && <div className="error">{error}</div>}
        {pick}
      </div>
    );
  }

  const width = cardCropWidth(media);
  return (
    <div className="stack">
      <CropFrame key={media.id} media={media} crop={crop} onChange={onCrop} ogGuide={ogGuide} />
      {previews(media, crop)}
      <div className="row between row-wrap">
        <span className="faint" style={{ fontSize: 13 }}>
          {media.originalName ?? t('foto')} · {media.width} × {media.height} · {formatBytes(media.bytes)}
        </span>
        <div className="row" style={{ gap: 6 }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => onCrop({ x: 0.5, y: 0.5 })} disabled={crop.x === 0.5 && crop.y === 0.5}>
            <IconMove /> {t('Centrar')}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => input.current?.click()} disabled={!!progress}>
            <IconUpload /> {progress ?? t('Cambiar foto')}
          </button>
        </div>
      </div>
      {width < 700 ? (
        <div className="notice warn">
          {t('La foto es chica ({width} × {height}): se verá ampliada y borrosa. Ideal: 1400 × 874 o más.', { width: media.width ?? '', height: media.height ?? '' })}
        </div>
      ) : width < 1400 ? (
        <div className="notice">{t('En pantallas de alta resolución se verá algo blanda: lo ideal es 1400 × 874 o más.')}</div>
      ) : null}
      {pick}
    </div>
  );
}

/**
 * La foto entera con el recuadro de la tarjeta (700 × 437) encima. Se arrastra (o se mueve con
 * las flechas) para elegir el encuadre. `crop` funciona como object-position de CSS.
 */
function CropFrame({ media, crop, onChange, ogGuide }: { media: Media; crop: Crop; onChange: (c: Crop) => void; ogGuide: boolean }) {
  const { t } = useT();
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  // Las medidas de la foto tal como se ve: si un JPG viene girado por EXIF, las que guardó la API son
  // las del archivo crudo (al revés). Se toman de la imagen ya cargada, que el navegador ya giró.
  const [shown, setShown] = useState<{ w: number; h: number } | null>(null);
  const W = shown?.w ?? media.width ?? 1;
  const H = shown?.h ?? media.height ?? 1;
  // El recuadro de la tarjeta, como fracción de la foto (igual que en la API y en phelon-moore-web).
  let cw = W;
  let ch = Math.round(W / CARD_RATIO);
  if (ch > H) {
    ch = H;
    cw = Math.round(H * CARD_RATIO);
  }
  const fw = cw / W;
  const fh = ch / H;
  const slackX = 1 - fw;
  const slackY = 1 - fh;
  // La imagen para compartir: el mismo recuadro, recortado arriba y abajo.
  const og = cw / OG_RATIO / ch;
  const clamp = (v: number) => Math.min(1, Math.max(0, Math.round(v * 1000) / 1000));

  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Sin esto, arrastrar fuera del recuadro selecciona el texto de la página.
    e.preventDefault();
    e.currentTarget.querySelector<HTMLElement>('.crop-box')?.focus({ preventScroll: true });
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, x: crop.x, y: crop.y };
  };
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const box = frame.current?.getBoundingClientRect();
    if (!d || !box) return;
    const dx = (e.clientX - d.px) / box.width;
    const dy = (e.clientY - d.py) / box.height;
    onChange({ x: slackX > 0.001 ? clamp(d.x + dx / slackX) : 0.5, y: slackY > 0.001 ? clamp(d.y + dy / slackY) : 0.5 });
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 0.1 : 0.02;
    const move: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const m = move[e.key];
    if (!m) return;
    e.preventDefault();
    onChange({ x: slackX > 0.001 ? clamp(crop.x + m[0]) : 0.5, y: slackY > 0.001 ? clamp(crop.y + m[1]) : 0.5 });
  };

  const axis = slackX > 0.001 && slackY > 0.001 ? 'move' : slackX > 0.001 ? 'ew-resize' : slackY > 0.001 ? 'ns-resize' : 'default';
  return (
    <div
      ref={frame}
      className="crop-frame"
      style={{ aspectRatio: `${W} / ${H}`, cursor: axis }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
    >
      <img
        src={media.url}
        alt=""
        draggable={false}
        onLoad={(e) => setShown({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
      />
      <div
        className="crop-box"
        role="slider"
        tabIndex={0}
        aria-label={t('Encuadre de la foto (flechas para mover)')}
        aria-valuetext={t('{x} % horizontal, {y} % vertical', { x: Math.round(crop.x * 100), y: Math.round(crop.y * 100) })}
        onKeyDown={onKey}
        style={{ left: `${slackX * crop.x * 100}%`, top: `${slackY * crop.y * 100}%`, width: `${fw * 100}%`, height: `${fh * 100}%` }}
      >
        {ogGuide && <div className="crop-og" style={{ top: `${((1 - og) / 2) * 100}%`, height: `${og * 100}%` }} />}
      </div>
    </div>
  );
}
